import React, { useMemo, useState } from "react";
import { format, parseISO, differenceInMonths, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { 
  Truck, Wrench, TrendingUp, TrendingDown, DollarSign, Clock, 
  AlertTriangle, CheckCircle2, BarChart3, PieChart as PieChartIcon
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, ComposedChart, Area
} from "recharts";

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const categoriaLabels = {
  veiculo: "Veículo",
  maquina_agricola: "Máquina Agrícola",
  implemento: "Implemento",
  equipamento: "Equipamento",
  edificacao: "Edificação",
  terreno: "Terreno",
  mobiliario: "Mobiliário",
  informatica: "Informática",
  outro: "Outro"
};

export default function RelatorioDesempenhoAtivos({
  ativos = [],
  manutencoes = [],
  propriedade,
  filtros = {}
}) {
  const [categoriaFiltro, setCategoriaFiltro] = useState("todas");

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Calcular métricas por ativo
  const ativosBase = useMemo(() => {
    return ativos.filter(a => {
      if (filtros.busca) {
        const b = filtros.busca.toLowerCase();
        if (!(a.nome || "").toLowerCase().includes(b) &&
            !(a.marca || "").toLowerCase().includes(b) &&
            !(a.modelo || "").toLowerCase().includes(b) &&
            !(a.numero_serie || "").toLowerCase().includes(b) &&
            !(a.placa || "").toLowerCase().includes(b)) {
          return false;
        }
      }
      if (filtros.categoria && filtros.categoria !== 'todas' && a.categoria !== filtros.categoria) return false;
      return true;
    });
  }, [ativos, filtros]);

  const metricsAtivos = useMemo(() => {
    return ativosBase.map(ativo => {
      const manutencoesAtivo = manutencoes.filter(m => m.ativo_id === ativo.id);
      
      // Custos de manutenção
      const custoManutencao = manutencoesAtivo.reduce((a, m) => a + (m.custo_total || 0), 0);
      const qtdManutencoes = manutencoesAtivo.length;
      
      // Tempo de posse
      const dataAquisicao = ativo.data_aquisicao ? parseISO(ativo.data_aquisicao) : new Date();
      const mesesPosse = differenceInMonths(new Date(), dataAquisicao);
      
      // Depreciação
      const vidaUtilMeses = ativo.vida_util_meses || 120;
      const valorResidual = ativo.valor_residual || 0;
      const valorAquisicao = ativo.valor_aquisicao || 0;
      const depreciacaoMensal = (valorAquisicao - valorResidual) / vidaUtilMeses;
      const depreciacaoAcumulada = Math.min(depreciacaoMensal * mesesPosse, valorAquisicao - valorResidual);
      const valorAtual = valorAquisicao - depreciacaoAcumulada;
      
      // Vida útil restante
      const vidaUtilRestante = Math.max(0, vidaUtilMeses - mesesPosse);
      const percentVidaUtil = ((vidaUtilMeses - mesesPosse) / vidaUtilMeses) * 100;
      
      // Taxa de manutenção (custo manutenção / valor ativo)
      const taxaManutencao = valorAquisicao > 0 ? (custoManutencao / valorAquisicao) * 100 : 0;
      
      // Custo mensal médio de manutenção
      const custoMensalManutencao = mesesPosse > 0 ? custoManutencao / mesesPosse : 0;
      
      // ROI estimado (considerando que o ativo gera valor = seu custo mensal de manutenção * 3)
      const valorGeradoEstimado = custoMensalManutencao * 3 * mesesPosse;
      const roi = valorAquisicao > 0 ? ((valorGeradoEstimado - custoManutencao) / valorAquisicao) * 100 : 0;
      
      // Status de manutenção
      const ultimaManutencao = manutencoesAtivo.length > 0 
        ? manutencoesAtivo.sort((a, b) => new Date(b.data_manutencao) - new Date(a.data_manutencao))[0]
        : null;
      
      const diasDesdeUltimaManutencao = ultimaManutencao 
        ? differenceInDays(new Date(), parseISO(ultimaManutencao.data_manutencao))
        : null;
      
      const precisaManutencao = diasDesdeUltimaManutencao && diasDesdeUltimaManutencao > 180;

      return {
        ...ativo,
        custoManutencao,
        qtdManutencoes,
        mesesPosse,
        depreciacaoAcumulada,
        valorAtual,
        vidaUtilRestante,
        percentVidaUtil,
        taxaManutencao,
        custoMensalManutencao,
        roi,
        diasDesdeUltimaManutencao,
        precisaManutencao,
        ultimaManutencao
      };
    });
  }, [ativosBase, manutencoes]);

  // Filtrar por categoria
  const ativosFiltrados = categoriaFiltro === "todas"
    ? metricsAtivos
    : metricsAtivos.filter(a => a.categoria === categoriaFiltro);

  // KPIs gerais
  const kpis = useMemo(() => {
    const dados = ativosFiltrados.filter(a => a.status === 'ativo' || a.status === 'em_manutencao');
    
    return {
      totalAtivos: dados.length,
      valorTotalAquisicao: dados.reduce((a, d) => a + (d.valor_aquisicao || 0), 0),
      valorAtualTotal: dados.reduce((a, d) => a + d.valorAtual, 0),
      depreciacaoTotal: dados.reduce((a, d) => a + d.depreciacaoAcumulada, 0),
      custoManutencaoTotal: dados.reduce((a, d) => a + d.custoManutencao, 0),
      ativosEmManutencao: dados.filter(a => a.status === 'em_manutencao').length,
      ativosPrecisandoManutencao: dados.filter(a => a.precisaManutencao).length,
      mediaVidaUtilRestante: dados.length > 0 
        ? dados.reduce((a, d) => a + d.vidaUtilRestante, 0) / dados.length 
        : 0
    };
  }, [ativosFiltrados]);

  // Dados para gráfico de custos por categoria
  const custoPorCategoria = useMemo(() => {
    const categorias = {};
    
    ativosFiltrados.forEach(a => {
      const cat = categoriaLabels[a.categoria] || a.categoria;
      if (!categorias[cat]) {
        categorias[cat] = { aquisicao: 0, manutencao: 0, valorAtual: 0 };
      }
      categorias[cat].aquisicao += a.valor_aquisicao || 0;
      categorias[cat].manutencao += a.custoManutencao || 0;
      categorias[cat].valorAtual += a.valorAtual || 0;
    });

    return Object.entries(categorias)
      .map(([name, valores]) => ({ name, ...valores }))
      .sort((a, b) => b.aquisicao - a.aquisicao);
  }, [ativosFiltrados]);

  // Dados para gráfico de manutenções por mês
  const manutencoesPorMes = useMemo(() => {
    const meses = {};
    const ativoIds = ativosFiltrados.map(a => a.id);
    
    manutencoes
      .filter(m => ativoIds.includes(m.ativo_id))
      .filter(m => {
        if (filtros.dataInicio && m.data_manutencao < filtros.dataInicio) return false;
        if (filtros.dataFim && m.data_manutencao > filtros.dataFim) return false;
        return true;
      })
      .forEach(m => {
        const mes = format(parseISO(m.data_manutencao), 'MMM/yy', { locale: ptBR });
        if (!meses[mes]) {
          meses[mes] = { preventiva: 0, corretiva: 0, preditiva: 0, custo: 0 };
        }
        meses[mes][m.tipo] = (meses[mes][m.tipo] || 0) + 1;
        meses[mes].custo += m.custo_total || 0;
      });

    return Object.entries(meses)
      .map(([mes, valores]) => ({ mes, ...valores }))
      .slice(-12);
  }, [manutencoes, ativosFiltrados, filtros]);

  // Top ativos com maior custo de manutenção
  const topCustoManutencao = ativosFiltrados
    .filter(a => a.custoManutencao > 0)
    .sort((a, b) => b.custoManutencao - a.custoManutencao)
    .slice(0, 5);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-semibold text-stone-800 mb-2">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {typeof entry.value === 'number' && entry.name.includes('Custo') 
                ? formatCurrency(entry.value) 
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="p-6 border-b border-stone-200 bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Truck className="w-7 h-7" />
                Desempenho de Ativos
              </h2>
              <p className="text-white/80 mt-1">{propriedade?.nome}</p>
            </div>
            <Select value={categoriaFiltro} onValueChange={setCategoriaFiltro}>
              <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as Categorias</SelectItem>
                {Object.entries(categoriaLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPIs */}
        <div className="p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-600 text-sm mb-1">
                <Truck className="w-4 h-4" />
                Total de Ativos
              </div>
              <p className="text-2xl font-bold text-blue-700">{kpis.totalAtivos}</p>
              <p className="text-xs text-blue-600 mt-1">
                {kpis.ativosEmManutencao} em manutenção
              </p>
            </CardContent>
          </Card>

          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
                <DollarSign className="w-4 h-4" />
                Valor Total (Aquisição)
              </div>
              <p className="text-xl font-bold text-green-700">{formatCurrency(kpis.valorTotalAquisicao)}</p>
            </CardContent>
          </Card>

          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-600 text-sm mb-1">
                <TrendingDown className="w-4 h-4" />
                Valor Atual (Líquido)
              </div>
              <p className="text-xl font-bold text-purple-700">{formatCurrency(kpis.valorAtualTotal)}</p>
              <p className="text-xs text-purple-600 mt-1">
                Dep: {formatCurrency(kpis.depreciacaoTotal)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-600 text-sm mb-1">
                <Wrench className="w-4 h-4" />
                Custo Manutenção
              </div>
              <p className="text-xl font-bold text-amber-700">{formatCurrency(kpis.custoManutencaoTotal)}</p>
              {kpis.ativosPrecisandoManutencao > 0 && (
                <Badge className="bg-red-100 text-red-700 mt-1">
                  {kpis.ativosPrecisandoManutencao} precisam manutenção
                </Badge>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Custos por Categoria */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Valor por Categoria
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={custoPorCategoria} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis 
                    type="number"
                    tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="valorAtual" name="Valor Atual" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="manutencao" name="Custo Manutenção" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Manutenções por Mês */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" />
              Histórico de Manutenções
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={manutencoesPorMes}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(v)}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="preventiva" name="Preventiva" stackId="a" fill="#22c55e" />
                  <Bar yAxisId="left" dataKey="corretiva" name="Corretiva" stackId="a" fill="#ef4444" />
                  <Bar yAxisId="left" dataKey="preditiva" name="Preditiva" stackId="a" fill="#3b82f6" />
                  <Line yAxisId="right" type="monotone" dataKey="custo" name="Custo Total" stroke="#8b5cf6" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Ativos com Maior Custo */}
      {topCustoManutencao.length > 0 && (
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Ativos com Maior Custo de Manutenção
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topCustoManutencao.map((ativo, idx) => (
                <div key={ativo.id} className="p-4 bg-stone-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-stone-800">{ativo.nome}</p>
                        <p className="text-sm text-stone-500">{ativo.marca} {ativo.modelo}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-amber-600">{formatCurrency(ativo.custoManutencao)}</p>
                      <p className="text-xs text-stone-500">{ativo.qtdManutencoes} manutenções</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 mt-3 text-sm">
                    <div className="p-2 bg-white rounded text-center">
                      <p className="text-stone-500 text-xs">Valor Aquisição</p>
                      <p className="font-medium">{formatCurrency(ativo.valor_aquisicao)}</p>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <p className="text-stone-500 text-xs">Taxa Manut.</p>
                      <p className="font-medium">{ativo.taxaManutencao.toFixed(1)}%</p>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <p className="text-stone-500 text-xs">Vida Útil Rest.</p>
                      <p className="font-medium">{ativo.vidaUtilRestante} meses</p>
                    </div>
                    <div className="p-2 bg-white rounded text-center">
                      <p className="text-stone-500 text-xs">Valor Atual</p>
                      <p className="font-medium">{formatCurrency(ativo.valorAtual)}</p>
                    </div>
                  </div>
                  <Progress value={ativo.percentVidaUtil} className="h-2 mt-3" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela Completa */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle>Análise Detalhada de Ativos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-stone-200">
                  <th className="text-left py-3 px-2 font-semibold">Ativo</th>
                  <th className="text-left py-3 px-2 font-semibold">Categoria</th>
                  <th className="text-right py-3 px-2 font-semibold">Aquisição</th>
                  <th className="text-right py-3 px-2 font-semibold">Valor Atual</th>
                  <th className="text-right py-3 px-2 font-semibold">Manutenção</th>
                  <th className="text-right py-3 px-2 font-semibold">Taxa</th>
                  <th className="text-center py-3 px-2 font-semibold">Vida Útil</th>
                  <th className="text-center py-3 px-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {ativosFiltrados.slice(0, 15).map(ativo => (
                  <tr key={ativo.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-2 px-2">
                      <p className="font-medium">{ativo.nome}</p>
                      <p className="text-xs text-stone-500">{ativo.marca}</p>
                    </td>
                    <td className="py-2 px-2">
                      <Badge variant="secondary">{categoriaLabels[ativo.categoria]}</Badge>
                    </td>
                    <td className="py-2 px-2 text-right">{formatCurrency(ativo.valor_aquisicao)}</td>
                    <td className="py-2 px-2 text-right text-purple-600 font-medium">{formatCurrency(ativo.valorAtual)}</td>
                    <td className="py-2 px-2 text-right text-amber-600">{formatCurrency(ativo.custoManutencao)}</td>
                    <td className="py-2 px-2 text-right">
                      <Badge className={ativo.taxaManutencao > 20 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                        {ativo.taxaManutencao.toFixed(1)}%
                      </Badge>
                    </td>
                    <td className="py-2 px-2 text-center">
                      <div className="w-full">
                        <Progress value={ativo.percentVidaUtil} className="h-2 mb-1" />
                        <span className="text-xs text-stone-500">{ativo.vidaUtilRestante} meses</span>
                      </div>
                    </td>
                    <td className="py-2 px-2 text-center">
                      {ativo.precisaManutencao ? (
                        <Badge className="bg-amber-100 text-amber-700">Revisar</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">OK</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
